import React, { useState } from 'react';
import * as Bixby from '$/core';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';
import './ClientApiAccess.scss';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import Modal from '$/components/global/modal';
import { ClientAdminService } from '$/services';
import { reloadCurrentClient } from '$/store/client/actions';
import { toastError, toastSuccess } from '$/store/toast/actions';

interface IClientApiAccessProps {
  isSuperAdmin: boolean;
  client?: Bixby.Models.Client;
  dispatchReloadCurrentClient: () => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchToastSuccess: (message: string) => Promise<void>;
}

const ClientApiAccess: React.FC<IClientApiAccessProps> = props => {
  const { isSuperAdmin, client, dispatchReloadCurrentClient, dispatchToastError, dispatchToastSuccess } = props;
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [regenerateModalActive, setRegenerateModalActive] = useState(false);
  const [clientApiCredentials, setClientApiCredentials] = useState(
    undefined as Bixby.Models.ClientApiCredentials | undefined
  );

  const clientId = client && !Bixby.Utils.isNullOrEmpty(client.clientId) ? client.clientId : undefined;

  const handleDeleteModalOpen = () => {
    setDeleteModalActive(true);
  };

  const handleRegenerateModalOpen = () => {
    setRegenerateModalActive(true);
  };

  const handleModalClose = () => {
    setRegenerateModalActive(false);
    setDeleteModalActive(false);
  };

  const handleCreateCredentials = async () => {
    if (client) {
      try {
        const creds = await ClientAdminService.createApiCredentials(client.id);
        setClientApiCredentials(creds);
        dispatchReloadCurrentClient();
        dispatchToastSuccess('API credentials created');
      } catch (e: any) {
        dispatchToastError('Failed to create API credentials');
      }
    }
  };

  const handleRegenerateCredentials = async () => {
    if (client) {
      try {
        const creds = await ClientAdminService.regenerateApiCredentials(client.id);
        setClientApiCredentials(creds);
        dispatchReloadCurrentClient();
        dispatchToastSuccess('API credentials regenerated');
        setRegenerateModalActive(false);
      } catch (e: any) {
        dispatchToastError('Failed to regenerate API credentials');
        setRegenerateModalActive(false);
      }
    }
  };

  const handleDeleteCredentials = async () => {
    if (client) {
      try {
        await ClientAdminService.deleteApiCredentials(client.id);
        setClientApiCredentials(undefined);
        dispatchReloadCurrentClient();
        dispatchToastSuccess('API credentials deleted');
        setDeleteModalActive(false);
      } catch (e: any) {
        dispatchToastError('Failed to revoke API credentials');
        setDeleteModalActive(false);
      }
    }
  };

  const section =
    clientApiCredentials || clientId ? (
      <div>
        {clientApiCredentials ? (
          <div>
            <InputBlock title="Client ID">
              <input type="text" value={clientApiCredentials.clientId} disabled />
            </InputBlock>
            <InputBlock title="Client Secret">
              <input type="text" value={clientApiCredentials.clientSecret} disabled />
            </InputBlock>
          </div>
        ) : (
          <div>
            <InputBlock title="Client ID">
              <input type="text" value={clientId} disabled />
            </InputBlock>
            <InputBlock title="Client Secret">
              <input type="password" value={Bixby.Constants.PASSWORD_PLACEHOLDER} disabled />
            </InputBlock>
          </div>
        )}
        <BixButton light type="button" onClick={handleDeleteModalOpen}>
          Revoke Access
        </BixButton>
        <BixButton type="button" onClick={handleRegenerateModalOpen}>
          Regenerate Credentials
        </BixButton>
      </div>
    ) : (
      <div>
        <p>There are no API Credentials for {client ? client.name : ''}</p>
        <BixButton type="button" onClick={handleCreateCredentials}>
          Create Credentials
        </BixButton>
      </div>
    );

  return client && isSuperAdmin ? (
    <section className="client_ApiAccess">
      {section}
      <Modal title="" active={deleteModalActive || regenerateModalActive} onClose={handleModalClose}>
        <div className="button_side_wrap">
          <p>
            {deleteModalActive
              ? `Are you sure you want to revoke API access for ${client.name}?`
              : `Are you sure you want to regenerate API credentials for ${client.name}?`}
          </p>
          <div className="button_side_wrap">
            {deleteModalActive ? (
              <BixButton type="button" onClick={handleDeleteCredentials}>
                Revoke
              </BixButton>
            ) : (
              <BixButton type="button" onClick={handleRegenerateCredentials}>
                Regenerate
              </BixButton>
            )}
            <BixButton light type="button" onClick={handleModalClose}>
              Cancel
            </BixButton>
          </div>
        </div>
      </Modal>
    </section>
  ) : null;
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isSuperAdmin,
    client: state.clientState.currentClient,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchReloadCurrentClient: () => dispatch(reloadCurrentClient()),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientApiAccess);
